/**
 * features/hr/usePublicHolidays.ts
 * ------------------------------------------------------------------
 * Live Botswana public-holiday list, backed by the `publicHolidays`
 * Firestore collection (doc id = YYYY-MM-DD). Falls back to the static
 * HOLIDAYS_2026 list when the collection is empty/unavailable, so the
 * attendance calendar and leave maths keep working before anything is
 * seeded. HR/admin edits are audited.
 *
 * Note on the fallback: the static list is used ONLY while the
 * collection is empty. As soon as one holiday is written, the live
 * collection becomes the single source of truth — so the admin UI
 * loads the built-in list in full before allowing edits, rather than
 * silently orphaning the defaults.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { collection, doc, onSnapshot, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { recordHrAudit, type HrAuditActor } from '@/lib/hrAudit';
import { HOLIDAYS_2026, type Holiday } from '@/lib/holidays';

const PUBLIC_HOLIDAYS_COLLECTION = 'publicHolidays';
const iso = () => new Date().toISOString();
const isIsoDate = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s);

export interface UsePublicHolidays {
  /** Sorted by date ascending. */
  holidays: Holiday[];
  /** date -> name (live, or the static fallback). */
  byDate: Map<string, string>;
  isHoliday: (dateStr: string) => boolean;
  holidayName: (dateStr: string) => string | undefined;
  /** True when served from Firestore; false when using the static fallback. */
  live: boolean;
  loading: boolean;
}

export function usePublicHolidays(): UsePublicHolidays {
  const [rows, setRows] = useState<Holiday[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, PUBLIC_HOLIDAYS_COLLECTION),
      (snap) => {
        const list = snap.docs.map((d) => {
          const data = d.data() as Partial<Holiday>;
          return { date: String(data.date ?? d.id), name: String(data.name ?? '') };
        });
        setRows(list);
        setLoading(false);
      },
      () => { setRows(null); setLoading(false); },
    );
    return unsub;
  }, []);

  return useMemo(() => {
    const live = !!rows && rows.length > 0;
    const source = live ? (rows as Holiday[]) : HOLIDAYS_2026;
    const sorted = [...source].sort((a, b) => a.date.localeCompare(b.date));
    const byDate = new Map(sorted.map((h) => [h.date, h.name]));
    return {
      holidays: sorted,
      byDate,
      isHoliday: (s: string) => byDate.has(s),
      holidayName: (s: string) => byDate.get(s),
      live,
      loading,
    };
  }, [rows, loading]);
}

/** Add or rename a public holiday (idempotent on the date). Audited. */
export async function savePublicHoliday(date: string, name: string, actor: HrAuditActor | null): Promise<void> {
  const d = date.trim();
  if (!isIsoDate(d)) throw new Error('Date must be in YYYY-MM-DD form.');
  const nm = name.trim();
  if (!nm) throw new Error('A holiday name is required.');
  await setDoc(
    doc(db, PUBLIC_HOLIDAYS_COLLECTION, d),
    { date: d, name: nm, updatedAt: iso(), updatedBy: actor?.id ?? null },
    { merge: true },
  );
  await recordHrAudit({ action: 'holiday.save', actor, subjectId: d, after: { date: d, name: nm } });
}

/** Delete a public holiday. Audited. */
export async function deletePublicHoliday(date: string, actor: HrAuditActor | null): Promise<void> {
  const d = date.trim();
  await deleteDoc(doc(db, PUBLIC_HOLIDAYS_COLLECTION, d));
  await recordHrAudit({ action: 'holiday.delete', actor, subjectId: d, before: { date: d } });
}

/** Bulk-load a gazette list (idempotent on date). Returns how many were written. Audited once. */
export async function importHolidays(list: Holiday[], actor: HrAuditActor | null): Promise<number> {
  let n = 0;
  for (const h of list) {
    if (!isIsoDate(h.date)) continue;
    await setDoc(
      doc(db, PUBLIC_HOLIDAYS_COLLECTION, h.date),
      { date: h.date, name: h.name, updatedAt: iso(), updatedBy: actor?.id ?? null },
      { merge: true },
    );
    n++;
  }
  await recordHrAudit({ action: 'holiday.import', actor, subjectId: PUBLIC_HOLIDAYS_COLLECTION, after: { count: n } });
  return n;
}
